import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Loader2, UserCog, Trash2, ShieldCheck, Search, KeyRound, Crown,
  UserCheck, UserX, Clock, Mail as MailIcon,
} from "lucide-react";

const ROLES = [
  { value: "super_admin", label: "Super Administrador", color: "bg-[#FBEDEF] text-[#7A1E2E] border-[#7A1E2E]/25" },
  { value: "admin", label: "Administrador", color: "bg-[#F5EEE0] text-[#7A1E2E] border-[#C19B6C]/50" },
  { value: "abogado", label: "Abogado", color: "bg-[#EEF6EE] text-[#1F5B2E] border-[#1F5B2E]/20" },
  { value: "asistente", label: "Asistente", color: "bg-white text-[#3d2a2a] border-[#E5DFD3]" },
  { value: "user", label: "Usuario", color: "bg-white text-[#6B5D57] border-[#E5DFD3]" },
];

const EMPTY_NEW = { name: "", email: "", password: "", role: "asistente" };
const EMPTY_EDIT = { name: "", role: "asistente", password: "" };
const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

function roleBadge(role) {
  const r = ROLES.find((x) => x.value === role);
  return <Badge className={`${r?.color || "bg-white"} font-normal border`}>{r?.label || role}</Badge>;
}

export default function PerfilesTab() {
  const { user: me, refresh } = useAuth();
  const isSuper = me?.role === "super_admin";
  const canManagePrincipal = me?.role === "super_admin" || me?.role === "admin";

  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState(null);       // { user, role }
  const [rejectTarget, setRejectTarget] = useState(null);         // user
  const [pendingBusy, setPendingBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_NEW);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [principalEmail, setPrincipalEmail] = useState("");
  const [principalDialog, setPrincipalDialog] = useState(null); // { user, action: 'set'|'unset' }
  const [savingPrincipal, setSavingPrincipal] = useState(false);

  const loadAll = () =>
    Promise.all([
      api.get("/users"),
      api.get("/despacho"),
      api.get("/users/pending"),
    ])
      .then(([ur, dr, pr]) => {
        setUsers(ur.data);
        setPrincipalEmail(((dr.data?.representante_email) || "").trim().toLowerCase());
        setPending(pr.data);
      })
      .catch(() => toast.error("Error cargando usuarios"))
      .finally(() => setLoading(false));

  useEffect(() => { loadAll(); }, []);

  const approveUser = async () => {
    if (!approveTarget) return;
    setPendingBusy(true);
    try {
      await api.post(`/users/${approveTarget.user.id}/approve`, { role: approveTarget.role || null });
      toast.success(`${approveTarget.user.name || approveTarget.user.email} aprobado`);
      setApproveTarget(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo aprobar");
    } finally { setPendingBusy(false); }
  };

  const rejectUser = async () => {
    if (!rejectTarget) return;
    setPendingBusy(true);
    try {
      await api.post(`/users/${rejectTarget.id}/reject`, { eliminar: true });
      toast.success("Solicitud rechazada");
      setRejectTarget(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo rechazar");
    } finally { setPendingBusy(false); }
  };

  const createUser = async () => {
    if (!newForm.name.trim() || !newForm.email.trim() || !newForm.password) {
      toast.error("Nombre, correo y contraseña son obligatorios"); return;
    }
    setSaving(true);
    try {
      await api.post("/users", newForm);
      toast.success("Usuario creado");
      setNewOpen(false);
      setNewForm(EMPTY_NEW);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al crear");
    } finally { setSaving(false); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", role: u.role || "user", password: "" });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const payload = {};
    if (editForm.name !== editing.name) payload.name = editForm.name;
    if (editForm.role !== editing.role) payload.role = editForm.role;
    if (editForm.password) payload.password = editForm.password;
    if (Object.keys(payload).length === 0) { setEditing(null); return; }
    setSaving(true);
    try {
      await api.put(`/users/${editing.id}`, payload);
      toast.success("Usuario actualizado");
      setEditing(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al actualizar");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Usuario eliminado");
      setDeleteTarget(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const applyPrincipal = async () => {
    if (!principalDialog) return;
    setSavingPrincipal(true);
    try {
      await api.post("/despacho/abogado-principal", {
        user_id: principalDialog.action === "set" ? principalDialog.user.id : null,
      });
      toast.success(
        principalDialog.action === "set"
          ? `${principalDialog.user.name || principalDialog.user.email} es ahora el Abogado Principal`
          : "Se retiró la marca de Abogado Principal"
      );
      setPrincipalDialog(null);
      await loadAll();
      // Refresh session flag in case the current user gained/lost the privilege
      await refresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setSavingPrincipal(false);
    }
  };

  const filtered = users.filter((u) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return [u.name, u.email, u.role].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const isPrincipal = (u) => principalEmail && (u.email || "").toLowerCase() === principalEmail;
  const principalUserObj = users.find((u) => isPrincipal(u));

  // Roles a las que este usuario puede asignar
  const assignableRoles = isSuper ? ROLES : ROLES.filter((r) => r.value !== "super_admin");

  return (
    <div className="space-y-5" data-testid="perfiles-tab">
      {/* Solicitudes de cuenta pendientes */}
      {pending.length > 0 && (
        <div className="rounded-xl bg-[#FFFBEF] border border-[#E7C875] p-4 space-y-3" data-testid="pending-accounts-panel">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-white border border-[#E7C875] flex items-center justify-center flex-shrink-0">
              <Clock className="h-4 w-4 text-[#B88537]" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-[#1A0810] flex items-center gap-2">
                Solicitudes de cuenta pendientes
                <Badge className="bg-[#7A1E2E] text-white border-0">{pending.length}</Badge>
              </div>
              <div className="text-[13px] text-[#7A5312]">
                Aprobá o rechazá las nuevas cuentas que se han registrado en el sistema.
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {pending.map((u) => (
              <div
                key={u.id}
                data-testid={`pending-user-${u.id}`}
                className="flex flex-wrap items-center gap-3 rounded-lg bg-white border border-[#E7C875]/60 px-3 py-2.5"
              >
                <Avatar className="h-10 w-10 ring-1 ring-[#E7C875]">
                  {u.avatar_url && <AvatarImage src={u.avatar_url} alt={u.name} />}
                  <AvatarFallback className="bg-[#B88537] text-white text-xs">{initials(u.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1A0810]">{u.name || "—"}</div>
                  <div className="text-xs text-[#6B5D57] flex items-center gap-1">
                    <MailIcon className="h-3 w-3" strokeWidth={1.8} /> {u.email}
                    {u.auth_provider === "google" && (
                      <Badge variant="outline" className="ml-2 border-[#E5DFD3] text-[#6B5D57] text-[10px]">Google</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-[#8A7563] mt-0.5">
                    Registrado {u.created_at ? new Date(u.created_at).toLocaleString("es-CR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </div>
                </div>
                <button
                  onClick={() => setApproveTarget({ user: u, role: "asistente" })}
                  data-testid={`pending-approve-${u.id}`}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-[#1F5B2E] hover:bg-[#144320] text-white text-xs font-semibold uppercase tracking-wider"
                  title="Aprobar cuenta"
                >
                  <UserCheck className="h-3.5 w-3.5" strokeWidth={2.2} /> Aprobar
                </button>
                <button
                  onClick={() => setRejectTarget(u)}
                  data-testid={`pending-reject-${u.id}`}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-[#7A1E2E] hover:bg-[#4A0E1C] text-white text-xs font-semibold uppercase tracking-wider"
                  title="Rechazar y eliminar"
                >
                  <UserX className="h-3.5 w-3.5" strokeWidth={2.2} /> Rechazar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Explicación del Abogado Principal */}
      <div className="rounded-xl bg-gradient-to-r from-[#F5EEE0] to-[#FBF9F4] border border-[#E5DFD3] p-4 flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-white border border-[#E5DFD3] flex items-center justify-center flex-shrink-0">
          <Crown className="h-4 w-4 text-[#B88537]" strokeWidth={2} fill="#B88537" />
        </div>
        <div className="text-sm text-[#3d2a2a] leading-relaxed">
          <div className="font-semibold text-[#1A0810]">Abogado Principal del bufete</div>
          <div className="mt-1 text-[13px]">
            Marca con la corona a un usuario para que sea el <span className="font-semibold text-[#7A1E2E]">único que pueda finalizar escrituras notariales</span> (estado Completada) y eliminarlas del registro.
            {principalUserObj ? (
              <> Actualmente es <span className="font-semibold text-[#1A0810]">{principalUserObj.name || principalUserObj.email}</span>.</>
            ) : (
              <span className="text-[#6B5D57]"> Aún no se ha designado uno.</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
          <Input
            data-testid="perfiles-search"
            placeholder="Buscar por nombre, correo, rol..."
            value={query} onChange={(e) => setQuery(e.target.value)}
            className={`${inputCls} pl-10`}
          />
        </div>
        <Button
          onClick={() => { setNewForm(EMPTY_NEW); setNewOpen(true); }}
          data-testid="perfiles-add-button"
          className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
        >
          <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Agregar usuario
        </Button>
      </div>

      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#6B5D57]"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck className="h-8 w-8 text-[#C19B6C] mx-auto" strokeWidth={1.4} />
            <p className="mt-3 text-sm text-[#6B5D57]">Sin resultados.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
                <TableHead className="text-[#3d2a2a] font-semibold w-14 text-center" title="Abogado Principal">
                  <Crown className="h-3.5 w-3.5 inline text-[#B88537]" strokeWidth={2} />
                </TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Usuario</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Rol</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Acceso</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => {
                const isSelf = u.id === me?.id;
                const isSuperTarget = u.role === "super_admin";
                const canDelete = isSuper && !isSelf && !isSuperTarget;
                const marked = isPrincipal(u);
                const canMarkThis = canManagePrincipal && (u.role !== "super_admin");
                return (
                  <TableRow
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className={`border-b border-[#E5DFD3] hover:bg-[#FBF9F4] ${marked ? "bg-[#FFFBEF]" : ""}`}
                  >
                    <TableCell className="text-center">
                      <button
                        type="button"
                        data-testid={`user-mark-principal-${u.id}`}
                        onClick={() => {
                          if (!canMarkThis) {
                            toast.error(u.role === "super_admin"
                              ? "El Super Admin no puede ser el Abogado Principal"
                              : "Solo Super Admin o Admin pueden designar al Abogado Principal");
                            return;
                          }
                          setPrincipalDialog({ user: u, action: marked ? "unset" : "set" });
                        }}
                        title={
                          marked
                            ? "Este usuario es el Abogado Principal. Clic para retirar la marca."
                            : (canMarkThis ? "Marcar como Abogado Principal" : "No disponible")
                        }
                        className={`h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors ${
                          marked
                            ? "bg-[#FFF4D1] border border-[#E7C875] hover:bg-[#FDE9A6]"
                            : "hover:bg-[#F5EEE0] border border-transparent"
                        } ${!canMarkThis && !marked ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <Crown
                          className="h-4 w-4"
                          strokeWidth={2}
                          fill={marked ? "#B88537" : "none"}
                          color={marked ? "#B88537" : "#C4B7A8"}
                        />
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 ring-1 ring-[#E5DFD3]">
                          {u.avatar_url && <AvatarImage src={u.avatar_url} alt={u.name} />}
                          <AvatarFallback className="bg-[#7A1E2E] text-[#F1EDE4] text-xs">{initials(u.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="text-sm font-medium text-[#1A0810] flex items-center gap-1.5">
                            {u.name || "—"}
                            {marked && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF4D1] border border-[#E7C875] text-[#8A5A00] text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wider">
                                <Crown className="h-2.5 w-2.5" strokeWidth={2.5} fill="#8A5A00" /> Abogado Principal
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#6B5D57]">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{roleBadge(u.role)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {u.active === false ? (
                          <Badge className="bg-[#F5EEE0] text-[#6B5D57] border border-[#E5DFD3]">Inactivo</Badge>
                        ) : (
                          <Badge className="bg-[#EEF6EE] text-[#1F5B2E] border border-[#1F5B2E]/20">Activo</Badge>
                        )}
                        {u.auth_provider === "google" && (
                          <Badge variant="outline" className="border-[#E5DFD3] text-[#6B5D57] text-[10px]">Google</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => openEdit(u)}
                          data-testid={`user-edit-${u.id}`}
                          className="h-8 w-8 hover:bg-[#F5EEE0]"
                          title="Editar"
                        >
                          <UserCog className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => setDeleteTarget(u)}
                          disabled={!canDelete}
                          data-testid={`user-delete-${u.id}`}
                          className="h-8 w-8 hover:bg-[#FBEDEF] disabled:opacity-30"
                          title={!canDelete ? (isSelf ? "No puede eliminarse a sí mismo" : isSuperTarget ? "No se puede eliminar un Super Admin" : "Solo Super Admin puede eliminar") : "Eliminar"}
                        >
                          <Trash2 className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Nuevo usuario */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg bg-[#FBF9F4] border-[#E5DFD3]" data-testid="user-new-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">Agregar usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className={labelCls}>Nombre completo *</Label>
              <Input data-testid="user-new-name" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} className={`${inputCls} mt-2`} placeholder="Lic. Ana Rojas" />
            </div>
            <div>
              <Label className={labelCls}>Correo electrónico *</Label>
              <Input data-testid="user-new-email" type="email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} className={`${inputCls} mt-2`} placeholder="arojas@bufete.cr" />
            </div>
            <div>
              <Label className={labelCls}>Contraseña temporal *</Label>
              <Input data-testid="user-new-password" type="text" value={newForm.password} onChange={(e) => setNewForm({ ...newForm, password: e.target.value })} className={`${inputCls} mt-2`} placeholder="Mínimo 6 caracteres" />
              <p className="mt-1 text-xs text-[#6B5D57]">El usuario podrá cambiarla al iniciar sesión.</p>
            </div>
            <div>
              <Label className={labelCls}>Rol *</Label>
              <Select value={newForm.role} onValueChange={(v) => setNewForm({ ...newForm, role: v })}>
                <SelectTrigger data-testid="user-new-role" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assignableRoles.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setNewOpen(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
            <Button onClick={createUser} disabled={saving} data-testid="user-new-save" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar usuario */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-[#FBF9F4] border-[#E5DFD3]" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">Editar usuario</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="rounded-lg bg-white border border-[#E5DFD3] p-3 text-sm text-[#6B5D57]">
                {editing.email}
              </div>
              <div>
                <Label className={labelCls}>Nombre</Label>
                <Input data-testid="user-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={`${inputCls} mt-2`} />
              </div>
              <div>
                <Label className={labelCls}>Rol</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                  disabled={editing.role === "super_admin" && !isSuper}
                >
                  <SelectTrigger data-testid="user-edit-role" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(isSuper ? ROLES : ROLES.filter((r) => r.value !== "super_admin")).map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Restablecer contraseña</Label>
                <div className="mt-2 relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
                  <Input
                    data-testid="user-edit-password"
                    type="text" value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    className={`${inputCls} pl-10`}
                    placeholder="Dejar vacío para no cambiar"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="user-edit-save" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Está seguro de eliminar a <b>{deleteTarget?.name || deleteTarget?.email}</b>? Perderá acceso al sistema inmediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="user-confirm-delete" className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar cambio de Abogado Principal */}
      <AlertDialog open={!!principalDialog} onOpenChange={(o) => !o && setPrincipalDialog(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810] flex items-center gap-2">
              <Crown className="h-5 w-5 text-[#B88537]" strokeWidth={2} fill="#B88537" />
              {principalDialog?.action === "set" ? "Designar Abogado Principal" : "Retirar marca de Abogado Principal"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              {principalDialog?.action === "set" ? (
                <>
                  Vas a designar a <b>{principalDialog?.user?.name || principalDialog?.user?.email}</b> como el nuevo <b>Abogado Principal</b> del bufete.
                  <div className="mt-2 rounded-md bg-[#FFF4D1] border border-[#E7C875] p-3 text-[13px] text-[#7A5312]">
                    Este usuario será el único que podrá <b>marcar escrituras como Completadas</b> y <b>eliminarlas</b> del registro notarial.
                  </div>
                  {principalUserObj && principalUserObj.id !== principalDialog?.user?.id && (
                    <div className="mt-2 text-[13px]">
                      Se retirará la marca de <b>{principalUserObj.name || principalUserObj.email}</b>.
                    </div>
                  )}
                </>
              ) : (
                <>
                  Vas a retirar la marca de Abogado Principal a <b>{principalDialog?.user?.name || principalDialog?.user?.email}</b>. Sin abogado principal designado, el privilegio pasa por defecto a los administradores.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={applyPrincipal}
              data-testid="btn-confirm-principal"
              disabled={savingPrincipal}
              className={principalDialog?.action === "set"
                ? "bg-[#B88537] hover:bg-[#8A6329] text-white"
                : "bg-[#6B5D57] hover:bg-[#4a3f39] text-white"}
            >
              {savingPrincipal ? <Loader2 className="h-4 w-4 animate-spin" /> :
                (principalDialog?.action === "set" ? "Confirmar designación" : "Retirar marca")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Aprobar cuenta */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent className="max-w-md bg-[#FBF9F4] border-[#E5DFD3]" data-testid="approve-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810] flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-[#1F5B2E]" strokeWidth={2} /> Aprobar cuenta
            </DialogTitle>
          </DialogHeader>
          {approveTarget && (
            <div className="space-y-4">
              <div className="rounded-lg bg-white border border-[#E5DFD3] p-3 text-sm">
                <div className="font-medium text-[#1A0810]">{approveTarget.user.name}</div>
                <div className="text-xs text-[#6B5D57]">{approveTarget.user.email}</div>
              </div>
              <div>
                <Label className={labelCls}>Asignar rol *</Label>
                <Select value={approveTarget.role} onValueChange={(v) => setApproveTarget({ ...approveTarget, role: v })}>
                  <SelectTrigger data-testid="approve-user-role" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
            <Button
              onClick={approveUser}
              disabled={pendingBusy}
              data-testid="approve-user-confirm"
              className="bg-[#1F5B2E] hover:bg-[#144320] text-white"
            >
              {pendingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aprobar cuenta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rechazar cuenta */}
      <AlertDialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810] flex items-center gap-2">
              <UserX className="h-5 w-5 text-[#7A1E2E]" strokeWidth={2} /> Rechazar solicitud
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              La cuenta <b>{rejectTarget?.name || rejectTarget?.email}</b> será eliminada del sistema. Podrá volver a registrarse si lo intenta más tarde.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={rejectUser}
              disabled={pendingBusy}
              data-testid="reject-user-confirm"
              className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]"
            >
              {pendingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rechazar y eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
